import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { 
  LayoutDashboard, 
  CalendarDays, 
  Settings,
  LogOut,
  Users,
  Calendar,
  TrendingUp,
  Clock,
  Search,
  MoreVertical,
  CheckCircle,
  XCircle,
  Utensils,
  Loader2,
  Menu,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Dashboard Overview Component
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentReservations, setRecentReservations] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reservationsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/reservations`, { credentials: 'include' })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (reservationsRes.ok) {
        const data = await reservationsRes.json();
        setRecentReservations(data.slice(0, 5));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const statCards = [
    { 
      title: 'Réservations totales', 
      value: stats?.total_reservations || 0, 
      icon: CalendarDays,
      color: 'text-blue-600 bg-blue-100'
    },
    { 
      title: "Aujourd'hui", 
      value: stats?.today_reservations || 0, 
      icon: Calendar,
      color: 'text-green-600 bg-green-100'
    },
    { 
      title: 'En attente', 
      value: stats?.pending_reservations || 0, 
      icon: Clock,
      color: 'text-orange-600 bg-orange-100'
    },
    { 
      title: "Couverts aujourd'hui", 
      value: stats?.today_guests || 0, 
      icon: Users,
      color: 'text-purple-600 bg-purple-100'
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Tableau de bord
        </h1>
        <p className="text-zinc-500">Vue d'ensemble de votre activité</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="border-zinc-100 shadow-sm" data-testid={`stat-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-zinc-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Recent Reservations */}
      <Card className="border-zinc-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Dernières réservations</CardTitle>
        </CardHeader>
        <CardContent>
          {recentReservations.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">Aucune réservation pour le moment</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Heure</TableHead>
                  <TableHead>Couverts</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentReservations.map((res) => (
                  <TableRow key={res.reservation_id}>
                    <TableCell className="font-medium">{res.guest_name}</TableCell>
                    <TableCell>{format(new Date(res.date), 'd MMM', { locale: fr })}</TableCell>
                    <TableCell>{res.time_slot}</TableCell>
                    <TableCell>{res.party_size}</TableCell>
                    <TableCell>
                      <StatusBadge status={res.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Reservations List Component
const ReservationsList = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reservations`, {
        credentials: 'include'
      });
      if (response.ok) {
        setReservations(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (reservationId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/reservations/${reservationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success('Statut mis à jour');
        fetchReservations();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Une erreur est survenue');
    }
  };

  const filteredReservations = reservations.filter(res => {
    const matchesFilter = filter === 'all' || res.status === filter;
    const matchesSearch = res.guest_name.toLowerCase().includes(search.toLowerCase()) ||
                         res.guest_email.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Réservations
        </h1>
        <p className="text-zinc-500">Gérez toutes vos réservations</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
            <SelectValue placeholder="Filtrer par statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="pending">En attente</SelectItem>
            <SelectItem value="confirmed">Confirmées</SelectItem>
            <SelectItem value="cancelled">Annulées</SelectItem>
            <SelectItem value="completed">Terminées</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-zinc-100 shadow-sm">
        <CardContent className="p-0">
          {filteredReservations.length === 0 ? (
            <p className="text-zinc-500 text-center py-12">Aucune réservation trouvée</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Heure</TableHead>
                  <TableHead>Couverts</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReservations.map((res) => (
                  <TableRow key={res.reservation_id} data-testid={`reservation-row-${res.reservation_id}`}>
                    <TableCell className="font-medium">{res.guest_name}</TableCell>
                    <TableCell className="text-zinc-500">{res.guest_email}</TableCell>
                    <TableCell>{format(new Date(res.date), 'd MMM yyyy', { locale: fr })}</TableCell>
                    <TableCell>{res.time_slot}</TableCell>
                    <TableCell>{res.party_size}</TableCell>
                    <TableCell>
                      <StatusBadge status={res.status} />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`actions-${res.reservation_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => updateStatus(res.reservation_id, 'confirmed')}>
                            <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                            Confirmer
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(res.reservation_id, 'completed')}>
                            <CheckCircle className="w-4 h-4 mr-2 text-blue-600" />
                            Terminer
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => updateStatus(res.reservation_id, 'cancelled')}>
                            <XCircle className="w-4 h-4 mr-2 text-red-600" />
                            Annuler
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Component
const SettingsPage = () => {
  const { user } = useAuth();
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope' }}>
          Paramètres
        </h1>
        <p className="text-zinc-500">Gérez les paramètres de votre compte</p>
      </div>

      <Card className="border-zinc-100 shadow-sm">
        <CardHeader>
          <CardTitle>Informations du compte</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm text-zinc-500">Nom</p>
            <p className="font-medium">{user?.name}</p>
          </div>
          <div>
            <p className="text-sm text-zinc-500">Email</p>
            <p className="font-medium">{user?.email}</p>
          </div>
          <div>
            <p className="text-sm text-zinc-500">Rôle</p>
            <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
              {user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800' },
    confirmed: { label: 'Confirmée', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Annulée', className: 'bg-red-100 text-red-800' },
    completed: { label: 'Terminée', className: 'bg-blue-100 text-blue-800' }
  };

  const { label, className } = config[status] || config.pending;

  return (
    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${className}`}>
      {label}
    </span>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/reservations', icon: CalendarDays, label: 'Réservations' },
    { path: '/admin/settings', icon: Settings, label: 'Paramètres' }
  ];

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-zinc-950 text-white p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Utensils className="w-6 h-6 text-orange-500" />
          <span className="font-bold text-lg" style={{ fontFamily: 'Manrope' }}>L'Ange Di Vin</span>
        </Link>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white hover:bg-zinc-800"
        >
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40
          w-64 bg-zinc-950 text-zinc-400
          transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 hidden lg:block">
            <Link to="/" className="flex items-center gap-2">
              <Utensils className="w-6 h-6 text-orange-500" />
              <span className="font-bold text-xl text-white" style={{ fontFamily: 'Manrope' }}>
                L'Ange Di Vin
              </span>
            </Link>
          </div>

          <nav className="mt-6 lg:mt-0 px-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-zinc-800 text-white'
                    : 'hover:bg-zinc-900 hover:text-zinc-200'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-zinc-800">
            <div className="flex items-center gap-3 px-2 mb-4">
              <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                ) : (
                  <span className="text-white font-medium">
                    {user?.name?.charAt(0) || 'A'}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                <p className="text-zinc-500 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Déconnexion
            </Button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="reservations" element={<ReservationsList />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
